<template>
    <span
        v-if="isVisible"
        class="jersey-number"
        :class="{ 'jersey-number--compact': size === 'compact' }"
        :style="jerseyStyle"
        :aria-label="ariaLabel"
        role="img"
    >
        <svg
            class="jersey-number__svg"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                d="M8.99991 4H5.99991L1.55691 5.777C1.40919 5.83615 1.29101 5.95155 1.22837 6.09783C1.16573 6.24411 1.16377 6.40927 1.22291 6.557L2.84891 10.623C2.89346 10.7343 2.97034 10.8297 3.06962 10.8969C3.16889 10.9641 3.28603 11 3.40591 11H5.99991V19.4C5.99991 19.5591 6.06312 19.7117 6.17564 19.8243C6.28817 19.9368 6.44078 20 6.59991 20H17.3999C17.559 20 17.7117 19.9368 17.8242 19.8243C17.9367 19.7117 17.9999 19.5591 17.9999 19.4V11H20.5939C20.7138 11 20.8309 10.9641 20.9302 10.8969C21.0295 10.8297 21.1064 10.7343 21.1509 10.623L22.7769 6.557C22.836 6.40927 22.8341 6.24411 22.7714 6.09783C22.7088 5.95155 22.5906 5.83615 22.4429 5.777L17.9999 4H14.9999C14.9999 4 14.9999 7 11.9999 7C8.99991 7 8.99991 4 8.99991 4Z"
                fill="var(--jersey-fill)"
                stroke="var(--jersey-stroke)"
                stroke-width="1"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
        </svg>
        <span
            v-if="hasNumber"
            class="jersey-number__digit"
            :class="{ 'jersey-number__digit--wide': isWideNumber }"
        >{{ displayNumber }}</span>
    </span>
</template>

<script setup>
import { computed } from 'vue';

const FALLBACK_PRIMARY = '#64748b';
const FALLBACK_SECONDARY = '#ffffff';
const STROKE = '#1f2937';

const props = defineProps({
    number: {
        type: [Number, String],
        default: null,
    },
    primaryColor: {
        type: String,
        default: null,
    },
    secondaryColor: {
        type: String,
        default: null,
    },
    size: {
        type: String,
        default: 'md', // md | compact
        validator: (value) => ['md', 'compact'].includes(value),
    },
    /** Show jersey shell without a number (kit color swatch). */
    showKit: {
        type: Boolean,
        default: false,
    },
});

const hasNumber = computed(() => {
    return props.number !== null && props.number !== undefined && props.number !== '';
});

const isVisible = computed(() => hasNumber.value || props.showKit);

const displayNumber = computed(() => String(props.number));

const isWideNumber = computed(() => displayNumber.value.length >= 2);

const ariaLabel = computed(() => {
    if (hasNumber.value) {
        return `Номер ${displayNumber.value}`;
    }
    return props.primaryColor ? `Цвет формы ${props.primaryColor}` : 'Цвет формы';
});

const jerseyStyle = computed(() => ({
    '--jersey-fill': props.primaryColor || FALLBACK_PRIMARY,
    '--jersey-stroke': STROKE,
    '--jersey-number': props.secondaryColor || FALLBACK_SECONDARY,
}));
</script>

<style scoped>
.jersey-number {
    --jersey-size: 2.625rem;

    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--jersey-size);
    height: var(--jersey-size);
    flex-shrink: 0;
    vertical-align: middle;
    line-height: 1;
}

.jersey-number--compact {
    --jersey-size: 2.25rem;
}

.jersey-number__svg {
    display: block;
    width: 100%;
    height: 100%;
}

.jersey-number__digit {
    position: absolute;
    left: 50%;
    top: 58%;
    transform: translate(-50%, -50%);
    color: var(--jersey-number);
    font-weight: 700;
    font-size: calc(var(--jersey-size) * 0.42);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.04em;
    line-height: 1;
    pointer-events: none;
    user-select: none;
    text-shadow: 0 0 1px rgba(0, 0, 0, 0.25);
}

.jersey-number__digit--wide {
    font-size: calc(var(--jersey-size) * 0.34);
}
</style>
